import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PRODUCT_HERO_IMAGE } from "@/lib/config";
import { useLang } from "@/lib/LanguageContext";

const HeroLine = ({ children, className = "" }) => (
    <span className={`mask-line ${className}`}>
        <span>{children}</span>
    </span>
);

export default function Hero() {
    const { t, chosen } = useLang();
    const [mounted, setMounted] = useState(false);
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });

    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
    const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

    useEffect(() => {
        // Only kick off the hero reveal after the user has picked a language
        // so the animation happens on the real "first look".
        if (!chosen) return;
        const t = setTimeout(() => setMounted(true), 60);
        return () => clearTimeout(t);
    }, [chosen]);

    const linesInClass = mounted ? "in" : "";

    return (
        <section
            id="top"
            ref={heroRef}
            className="relative pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden"
            data-testid="hero-section"
        >
            <div className="container-x">
                <div className="grid grid-cols-12 gap-6 md:gap-10">
                    <motion.div
                        style={{ y: textY }}
                        className="col-span-12 lg:col-span-7 flex flex-col justify-center"
                    >
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: mounted ? 1 : 0 }}
                            transition={{ duration: 0.6 }}
                            className="flex items-center gap-3 mb-8"
                            data-testid="hero-eyebrow"
                        >
                            <span className="h-px w-10 bg-[#B48F57]" />
                            <span className="eyebrow text-[#B48F57]">
                                {t("hero.eyebrow")}
                            </span>
                        </motion.div>

                        <h1
                            className="font-serif text-[52px] leading-[1.03] tracking-[-0.02em] sm:text-[72px] md:text-[96px] lg:text-[112px] text-[#2D2824] font-light"
                            data-testid="hero-headline"
                        >
                            <HeroLine className={`d1 ${linesInClass}`}>
                                {t("hero.h1a")}
                            </HeroLine>
                            <HeroLine className={`d2 ${linesInClass}`}>
                                {t("hero.h1b_pre")}
                                <em className="italic text-[#B48F57] font-light">
                                    {t("hero.h1b_em")}
                                </em>
                            </HeroLine>
                        </h1>

                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{
                                opacity: mounted ? 1 : 0,
                                y: mounted ? 0 : 12,
                            }}
                            transition={{ duration: 0.8, delay: 0.75 }}
                            className="mt-10 max-w-[46ch] text-[15px] md:text-[17px] leading-relaxed text-[#5C564D] font-light"
                            data-testid="hero-subtext"
                        >
                            {t("hero.sub")}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{
                                opacity: mounted ? 1 : 0,
                                y: mounted ? 0 : 12,
                            }}
                            transition={{ duration: 0.8, delay: 0.95 }}
                            className="mt-12 flex flex-wrap items-center gap-4"
                        >
                            <a
                                href="#order"
                                onClick={(e) => {
                                    e.preventDefault();
                                    document
                                        .getElementById("order")
                                        ?.scrollIntoView({ behavior: "smooth" });
                                }}
                                className="btn btn-dark"
                                data-testid="hero-order-cta"
                            >
                                {t("hero.cta_order")}
                                <span aria-hidden>→</span>
                            </a>
                            <a
                                href="#ritual"
                                onClick={(e) => {
                                    e.preventDefault();
                                    document
                                        .getElementById("ritual")
                                        ?.scrollIntoView({ behavior: "smooth" });
                                }}
                                className="btn btn-ghost"
                                data-testid="hero-learn-cta"
                            >
                                {t("hero.cta_ritual")}
                            </a>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: mounted ? 1 : 0 }}
                            transition={{ duration: 1, delay: 1.15 }}
                            className="mt-14 flex items-center gap-6 text-[11px] tracking-[0.2em] uppercase text-[#8A837A]"
                            data-testid="hero-meta"
                        >
                            <span>{t("hero.meta_ml")}</span>
                            <span className="h-3 w-px bg-[#D9D1C7]" />
                            <span>{t("hero.meta_herbal")}</span>
                            <span className="h-3 w-px bg-[#D9D1C7]" />
                            <span>{t("hero.meta_cod")}</span>
                        </motion.div>
                    </motion.div>

                    <div className="col-span-12 lg:col-span-5 relative min-h-[420px] md:min-h-[560px] flex items-center justify-center spotlight">
                        <motion.div
                            style={{ y: imgY, scale: imgScale }}
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{
                                opacity: mounted ? 1 : 0,
                                scale: mounted ? 1 : 0.94,
                            }}
                            transition={{
                                duration: 1.4,
                                delay: 0.35,
                                ease: [0.19, 1, 0.22, 1],
                            }}
                            className="relative z-10 w-full h-full flex items-center justify-center"
                        >
                            <img
                                src={PRODUCT_HERO_IMAGE}
                                alt="VARDHAN Ayurvedic Detox and Recovery Support — 2 x 50ml"
                                className="w-full max-w-[520px] object-contain drop-shadow-[0_35px_60px_rgba(45,40,36,0.22)]"
                                data-testid="hero-product-image"
                                loading="eager"
                            />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: mounted ? 1 : 0 }}
                            transition={{ duration: 1, delay: 1.3 }}
                            className="absolute top-6 right-2 md:right-6 z-20 text-right"
                        >
                            <div className="chapter-num">{t("hero.chapter")}</div>
                            <div className="text-[11px] tracking-[0.22em] uppercase text-[#5C564D] mt-1">
                                {t("hero.since")}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: mounted ? 1 : 0 }}
                            transition={{ duration: 1, delay: 1.5 }}
                            className="hidden md:flex absolute bottom-2 left-2 items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-[#8A837A]"
                        >
                            <span className="block h-px w-14 bg-[#8A837A] animate-pulse" />
                            {t("hero.scroll")}
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}
